import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import { api, API_URL, tokenStore } from "../../lib/api";
import { Download } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ event_id: "", start: "", end: "" });

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const buildUrl = (kind) => {
    const qs = new URLSearchParams();
    if (filters.event_id) qs.set("event_id", filters.event_id);
    if (filters.start) qs.set("start", filters.start);
    if (filters.end) qs.set("end", filters.end);
    return `${API_URL}/reports/attendance.${kind}?${qs.toString()}`;
  };

  const download = async (kind) => {
    try {
      const res = await fetch(buildUrl(kind), {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${kind.toUpperCase()}`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const downloadRoster = async (kind) => {
    if (!filters.event_id) {
      toast.error("Choose an event first");
      return;
    }
    try {
      const res = await fetch(
        `${API_URL}/reports/event-roster.${kind}?event_id=${encodeURIComponent(filters.event_id)}`,
        { headers: { Authorization: `Bearer ${tokenStore.get()}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `event-roster.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Roster downloaded`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Org Admin" title="Reports" />

      <div className="border border-[#D2D8D4] rounded-md bg-white p-5 mb-6 grid md:grid-cols-4 gap-3 items-end">
        <Field label="Event">
          <select
            value={filters.event_id}
            onChange={(e) => setFilters({ ...filters, event_id: e.target.value })}
            className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md bg-white"
            data-testid="rep-event"
          >
            <option value="">All events</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Start date">
          <input type="date" value={filters.start}
            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
            className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="rep-start" />
        </Field>
        <Field label="End date">
          <input type="date" value={filters.end}
            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
            className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="rep-end" />
        </Field>
        <div className="flex gap-2">
          <button
            onClick={() => download("csv")}
            className="flex-1 px-4 py-2 rounded-md border border-[#D2D8D4] font-semibold hover:border-[#0f1a14] hover:bg-[#F4F6F5] flex items-center justify-center gap-2 transition-colors"
            data-testid="dl-csv"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={() => download("xlsx")}
            className="flex-1 px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] flex items-center justify-center gap-2 transition-colors"
            data-testid="dl-xlsx"
          >
            <Download className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      <div className="border border-[#E4E4E7] bg-white p-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#05126E] mb-2">Tip</div>
        <p className="text-sm text-zinc-700">
          Filter by event or date range and export the result as CSV or Excel.
          Reports include Member ID, member name, event, timestamp, and
          attendance status (On time / Late).
        </p>
      </div>

      <div className="mt-6 border border-[#D2D8D4] rounded-md bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#05126E]">
              Event roster (with absentees)
            </div>
            <h2 className="text-xl font-extrabold text-[#0f1a14] mt-1">
              Per-event sheet
            </h2>
            <p className="text-sm text-[#5a6663]">
              Full member list with their attendance status for one event —
              <strong> On time, Late, or Absent</strong>.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <Field label="Event">
            <select
              value={filters.event_id}
              onChange={(e) => setFilters({ ...filters, event_id: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md bg-white"
              data-testid="roster-event-select"
            >
              <option value="">Choose event…</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </Field>
          <button
            onClick={() => downloadRoster("csv")}
            disabled={!filters.event_id}
            className="px-4 py-2 rounded-md border border-[#D2D8D4] font-semibold hover:border-[#0f1a14] hover:bg-[#F4F6F5] flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            data-testid="dl-roster-csv"
          >
            <Download className="w-4 h-4" /> Roster CSV
          </button>
          <button
            onClick={() => downloadRoster("xlsx")}
            disabled={!filters.event_id}
            className="px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            data-testid="dl-roster-xlsx"
          >
            <Download className="w-4 h-4" /> Roster Excel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
